import React, { useCallback, useEffect, useState } from "react";
import {

  DashboardUpcomingReleaseParams,
  DashboardUpcomingReleaseResponse,
  useMusicApi
} from "../../api/useMusicApi";
import { InputSelect } from "../../components/InputSelect";
import { BooleanParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import { useContractors } from "../../contexts/ContractorsContext";
import { PartialNullable } from "../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRecordVinyl } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../components/Button";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { useLoading } from "../../hooks/use-loading";
import { toast } from "react-toastify";
import { useTranslation } from "../../hooks/useTranslation";
import { Preloader } from "../../components/Preloader";
import { EmptyTablePlaceholder } from "../../components/Table/EmptyTablePlaceholder";
import { SimpleViewModal } from "../../components/Modals/SimpleViewModal";
import cn from "classnames";

type Props = {
  contractorId: number | null;
}

export const UpcomingReleasesWidget: React.FC<Props> = ({ contractorId }) => {
  const { getDashboardUpcomingReleases } = useMusicApi();
  const { t } = useTranslation();

  const { loadings, stopLoading, startLoading } = useLoading({
    fetchData: contractorId != null
  });

  const [showModal, setShowModal] = useState<boolean>(false);
  const [data, setData] = useState<DashboardUpcomingReleaseResponse | null>(null);
  const [queryParams, setQueryParams] = useQueryParams({
    week_id: StringParam,
    full_list: withDefault(BooleanParam, true)
  });

  useEffect(() => {
    if (contractorId != null) {
      void fetchData({ ...queryParams, contractor_id: contractorId } as any);
    }
  }, [contractorId, queryParams]);

  const fetchData = useCallback(async (params: PartialNullable<DashboardUpcomingReleaseParams>) => {
    if (params.contractor_id == null) {
      return;
    }
    startLoading("fetchData");
    try {
      const response = await getDashboardUpcomingReleases(params);
      setQueryParams({ week_id: response.current_week_id as unknown as string });
      setData(response);
    } catch (e) {
      toast.error(t("unexpected error"));
    } finally {
      stopLoading("fetchData");
    }
  }, []);

  useEffect(() => {
    void fetchData(queryParams as any);
  }, [queryParams]);

  return <>
    <div className={"w-full border border-gray-500 rounded p-[18px] h-full min-h-[240px] flex flex-col"}>
      <div className={"flex items-center justify-between"}>
        <div className={"font-[500] text-[16px]"}>
          {t("dashboard.top_release_by_week")}
        </div>
        <div className={"flex gap-[10px]"}>
          <InputSelect
            placeholder={"Неделя"}
            className={"w-[210px]"}
            value={queryParams.week_id ?? null}
            options={data?.available_weeks?.map(w => {
              return {
                label: w.week_title,
                value: w.week_id
              };
            }) ?? []}
            onChange={(v) => setQueryParams({ week_id: v.value?.toString() })}
          />
        </div>
      </div>
      <div className={cn("flex flex-col text-[13px] mt-[24px] h-full", {
        "justify-center": (data?.data?.length ?? 0) === 0
      })}>
        {loadings.fetchData && !showModal && <Preloader size={"3x"} className={"text-muted"} />}
        {
          !loadings.fetchData && ((data?.data?.length ?? 0) > 0)
            ? data?.data?.slice(0, 10).map(r => {
              return <div
                key={r.full_title}
                className={"flex gap-[12px] p-[8px] rounded-[24px] items-center justify-between hover:bg-gray-100"}
              >
                <div className={"flex gap-[12px] items-center"}>
                  <img alt={"logo"} width={32} height={32} className={"rounded-full border"} src={r.cover_url} />
                  <div className={"max-w-[400px] truncate"}>{r.full_title}</div>
                </div>
                <div className={"flex gap-[12px] items-center"}>
                  <div className={"flex items-center gap-[4px] w-[50px] text-left"}>
                    <FontAwesomeIcon className={"text-[13px] text-gray-400"} icon={faRecordVinyl} />{r.number_of_tracks}
                  </div>
                  <div className={"w-[70px] text-right"}>{r.release_date}</div>
                </div>
              </div>;
            })
            : !loadings.fetchData && <EmptyTablePlaceholder className={"my-0"} />
        }

        {
          !loadings.fetchData && (data?.data?.length ?? 0 > 0)
            ? <div className={"mt-2 w-full flex justify-center"}>
              <Button
                label={t("dashboard.show_all").toString()}
                className={"w-fit"}
                icon={faEye}
                onClick={() => {
                  setShowModal(true);
                }}
              /></div>
            : null
        }
      </div>

    </div>
    {
      showModal && <SimpleViewModal title={t("dashboard.top_release_by_week")} onCancel={() => setShowModal(false)}>
        <div className={"flex flex-col text-[13px] mt-[24px]"}>
          {loadings.fetchData && <Preloader size={"3x"} className={"text-muted"} />}
          {
            !loadings.fetchData && (data?.data?.length ?? 0) > 0
              ? data?.data?.map(r => {
                return <div
                  key={r.full_title}
                  className={"flex gap-[12px] p-[12px] rounded-[24px] items-center justify-between hover:bg-gray-100"}>
                  <div className={"flex gap-[12px] items-center"}>
                    <img alt={"logo"} width={32} height={32} className={"rounded-full border"} src={r.cover_url} />
                    <div className={"max-w-[400px] truncate"}>{r.full_title}</div>
                  </div>
                  <div className={"flex gap-[12px] items-center"}>
                    <div className={"flex items-center gap-[4px] w-[50px] text-left"}>
                      <FontAwesomeIcon className={"text-[13px] text-gray-400"} icon={faRecordVinyl} />{r.number_of_tracks}
                    </div>
                    <div className={"w-[70px] text-right"}>{r.release_date}</div>
                  </div>
                </div>;
              })
              : !loadings.fetchData && <EmptyTablePlaceholder />
          }
        </div>
      </SimpleViewModal>
    }

  </>;
};
